import { Card, Statistic, Tag, Typography } from 'antd';
import { StarOutlined, GlobalOutlined, EnvironmentOutlined } from '@ant-design/icons';
import type {
  QuestionAnalysisResult,
  GroupInfo,
  ProfileField,
  ChoiceSegmentOption,
  BooleanSummary,
} from '../../services/surveyAnalysis.service';

const { Text } = Typography;

const BAR_COLORS = [
  '#2E2B81',
  '#5856D6',
  '#34C759',
  '#FF9500',
  '#FF3B30',
  '#007AFF',
  '#AF52DE',
  '#FF2D55',
];

// ============================================================================
// Type guards for segment values
// ============================================================================

function isChoiceSegmentArray(v: unknown): v is ChoiceSegmentOption[] {
  return Array.isArray(v) && (v.length === 0 || ('option' in (v[0] ?? {})));
}

function isNumericSegment(v: unknown): v is number {
  return typeof v === 'number';
}

function isBooleanSegment(v: unknown): v is BooleanSummary {
  return typeof v === 'object' && v !== null && 'yes' in v && 'no' in v;
}

// ============================================================================
// Segment renderers
// ============================================================================

function ChoiceSegmentView({ data }: { data: ChoiceSegmentOption[] }) {
  const sorted = [...data].sort((a, b) => b.count - a.count);
  const maxCount = Math.max(...sorted.map((o) => o.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {sorted.map((opt, idx) => {
        const barWidth = (opt.count / maxCount) * 100;
        const color = BAR_COLORS[idx % BAR_COLORS.length];
        const shouldShowCountInsideBar = barWidth > 25 || opt.count === 1;

        return (
          <div key={opt.option} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ width: 120, flexShrink: 0 }}>
              <Text style={{ fontSize: 12, display: 'block', lineHeight: 1.3 }} title={opt.option}>
                {opt.option}
              </Text>
            </div>
            <div style={{ flex: 1, paddingTop: 4 }}>
              <div
                style={{
                  background: '#f0f0f0',
                  borderRadius: 4,
                  height: 24,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: color,
                    height: '100%',
                    width: `${barWidth}%`,
                    borderRadius: 4,
                    transition: 'width 0.4s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: 6,
                    minWidth: barWidth > 0 ? 22 : 0,
                  }}
                >
                  {shouldShowCountInsideBar && (
                    <Text style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                      {opt.count}
                    </Text>
                  )}
                </div>
              </div>
            </div>
            <div style={{ width: 60, textAlign: 'right', flexShrink: 0, paddingTop: 4 }}>
              <Text style={{ fontSize: 12 }}>
                {opt.count}{' '}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ({opt.percent}%)
                </Text>
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RatingSegmentView({ average }: { average: number }) {
  return (
    <Statistic
      value={average}
      precision={2}
      prefix={<StarOutlined />}
      valueStyle={{ color: '#faad14', fontSize: 22 }}
    />
  );
}

function BooleanSegmentView({ data }: { data: BooleanSummary }) {
  const maxCount = Math.max(data.yes.count, data.no.count, 1);

  const items = [
    { label: 'Sim', color: '#52c41a', count: data.yes.count, percent: data.yes.percent },
    { label: 'Não', color: '#ff4d4f', count: data.no.count, percent: data.no.percent },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item) => {
        const barWidth = (item.count / maxCount) * 100;
        const shouldShowCountInsideBar = barWidth > 25 || item.count === 1;

        return (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, flexShrink: 0 }}>
              <Text style={{ fontSize: 12 }}>{item.label}</Text>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  background: '#f0f0f0',
                  borderRadius: 4,
                  height: 24,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: item.color,
                    height: '100%',
                    width: `${barWidth}%`,
                    borderRadius: 4,
                    transition: 'width 0.4s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: 6,
                    minWidth: barWidth > 0 ? 22 : 0,
                  }}
                >
                  {shouldShowCountInsideBar && (
                    <Text style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                      {item.count}
                    </Text>
                  )}
                </div>
              </div>
            </div>
            <div style={{ width: 60, textAlign: 'right', flexShrink: 0 }}>
              <Text style={{ fontSize: 12 }}>
                {item.count}{' '}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ({item.percent}%)
                </Text>
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Group header
// ============================================================================

function GroupScopeTag({ group }: { group: GroupInfo }) {
  if (group.scope === 'NATIONAL') {
    return (
      <Tag icon={<GlobalOutlined />} color="blue">
        Nacional
      </Tag>
    );
  }
  const stateLabel = group.state_name ?? group.state_code ?? '';
  return (
    <Tag icon={<EnvironmentOutlined />} color="orange">
      Estadual — {stateLabel}
    </Tag>
  );
}

function GroupHeader({ group }: { group: GroupInfo }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0 4px 0',
        borderBottom: '2px solid #e8e8e8',
        marginBottom: 12,
      }}
    >
      <Text strong style={{ fontSize: 15 }}>
        {group.name}
      </Text>
      <GroupScopeTag group={group} />
    </div>
  );
}

// ============================================================================
// Per-question card
// ============================================================================

const QUESTION_TYPE_TAG: Record<string, { label: string; color: string }> = {
  MULTIPLE_CHOICE: { label: 'Escolha única', color: 'blue' },
  CHECKBOX: { label: 'Múltipla escolha', color: 'purple' },
  RATING: { label: 'Avaliação', color: 'gold' },
};

function QuestionSegmentCard({
  q,
  criterion,
}: {
  q: QuestionAnalysisResult;
  criterion: ProfileField;
}) {
  const analyticsForField = q.analytics[criterion];
  const typeInfo = QUESTION_TYPE_TAG[q.question_type];

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{q.short_description}</span>
          {typeInfo && <Tag color={typeInfo.color} style={{ flexShrink: 0, margin: 0 }}>{typeInfo.label}</Tag>}
        </div>
      }
      size="small"
    >
      {!analyticsForField || Object.keys(analyticsForField).length === 0 ? (
        <Text type="secondary">Sem dados de segmentação disponíveis.</Text>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {Object.entries(analyticsForField).map(([groupLabel, segmentData]) => (
            <Card
              key={groupLabel}
              size="small"
              title={<Text strong>{groupLabel}</Text>}
              bordered
              style={{ backgroundColor: '#fafafa' }}
            >
              {isChoiceSegmentArray(segmentData) && (
                <ChoiceSegmentView data={segmentData} />
              )}
              {isNumericSegment(segmentData) && (
                <RatingSegmentView average={segmentData} />
              )}
              {isBooleanSegment(segmentData) && (
                <BooleanSegmentView data={segmentData} />
              )}
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Main component
// ============================================================================

interface AnalyticsSegmentedViewProps {
  questions: QuestionAnalysisResult[];
  criterion: ProfileField;
}

export function AnalyticsSegmentedView({
  questions,
  criterion,
}: AnalyticsSegmentedViewProps) {
  if (questions.length === 0) {
    return (
      <Text type="secondary">Nenhuma pergunta com dados estatísticos disponível.</Text>
    );
  }

  const hasGroups = questions.some((q) => q.group != null);

  if (!hasGroups) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {questions.map((q) => (
          <QuestionSegmentCard key={q.question_id} q={q} criterion={criterion} />
        ))}
      </div>
    );
  }

  // Build ordered list of unique groups preserving the group.order sequence
  const groupMap = new Map<string, { info: GroupInfo; questions: QuestionAnalysisResult[] }>();
  const ungrouped: QuestionAnalysisResult[] = [];

  for (const q of questions) {
    if (q.group == null) {
      ungrouped.push(q);
    } else {
      if (!groupMap.has(q.group.id)) {
        groupMap.set(q.group.id, { info: q.group, questions: [] });
      }
      groupMap.get(q.group.id)!.questions.push(q);
    }
  }

  const orderedGroups = Array.from(groupMap.values()).sort(
    (a, b) => a.info.order - b.info.order
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {orderedGroups.map(({ info, questions: groupQs }) => (
        <div key={info.id}>
          <GroupHeader group={info} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {groupQs.map((q) => (
              <QuestionSegmentCard key={q.question_id} q={q} criterion={criterion} />
            ))}
          </div>
        </div>
      ))}
      {ungrouped.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ungrouped.map((q) => (
            <QuestionSegmentCard key={q.question_id} q={q} criterion={criterion} />
          ))}
        </div>
      )}
    </div>
  );
}

export default AnalyticsSegmentedView;
